// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n, getCurrencySymbol } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Button from 'shared/components/common/Button';
import Icon from 'shared/components/common/Icon';
import Page from 'main-app/components/Page';
import ApprovePurchaseOrderMutation from 'main-app/mutations/ApprovePurchaseOrder';
import * as Actions from 'main-app/store/Actions';
import moment from 'moment';

type Props = {
  history: RouterHistory,
};

type State = {
  expandedRows: Set<string>,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CountBadge = styled.span`
  background-color: #f59e0b;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 16px;
`;

const ExpandedSection = styled.div`
  padding: 16px;
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
`;

const LineItemsTable = styled.div`
  margin-top: 8px;
`;

const LineItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;

  &:first-child {
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    font-size: 12px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

class ApprovalDashboard extends PureComponent<Props, State> {
  state = {
    expandedRows: new Set(),
  };

  handleApprove = async (poId: string, poNumber: string, refetch: Function) => {
    if (!poId) {
      Actions.alertNotification(
        i18n.t('Error: Purchase Order ID is missing'),
        'error'
      );
      return;
    }

    Actions.alertNotification(
      i18n.t('Approving purchase order...'),
      'info'
    );

    try {
      const response = await ApprovePurchaseOrderMutation.commit({
        variables: {
          input: {
            id: poId,
            status: 'APPROVED',
          },
        },
      });

      if (response.updatePurchaseOrderStatus?.purchaseOrder) {
        Actions.alertNotification(
          i18n.t(`Purchase order ${poNumber} approved successfully!`),
          'success'
        );

        // Refetch to update the list
        setTimeout(() => {
          refetch();
        }, 1500);
      } else {
        Actions.alertNotification(
          i18n.t('Failed to approve purchase order'),
          'error'
        );
      }
    } catch (e) {
      Actions.alertNotification(
        i18n.t(`Failed to approve: ${e.message}`),
        'error'
      );
    }
  };

  toggleRowExpansion = (poId: string) => {
    const { expandedRows } = this.state;
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(poId)) {
      newExpandedRows.delete(poId);
    } else {
      newExpandedRows.add(poId);
    }
    
    this.setState({ expandedRows: newExpandedRows });
  };

  formatCurrency = (amount: ?number) => {
    if (!amount) return `${getCurrencySymbol()}0.00`;
    return `${getCurrencySymbol()}${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  formatDate = (date: ?string) => {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY HH:mm');
  };

  render() {
    const { history } = this.props;
    const { expandedRows } = this.state;

    return (
      <Page title={i18n.t('Purchase Order Approvals')} history={history}>
        <QueryRenderer
          environment={relayEnvironment}
          dataFrom="STORE_THEN_NETWORK"
          query={graphql`
            query ApprovalDashboardQuery($first: Int) {
              poApprovalQueue(first: $first)
                @connection(key: "ApprovalDashboard_poApprovalQueue") {
                edges {
                  node {
                    id
                    poNumber
                    vendor {
                      id
                      vendorNumber
                      name
                    }
                    status
                    totalAmount
                    requestedBy {
                      id
                      firstName
                      lastName
                    }
                    createdAt
                    lineItems {
                      id
                      item {
                        id
                        itemNumber
                        name
                      }
                      quantity
                      unitPrice
                      totalPrice
                    }
                  }
                }
                totalCount
              }
            }
          `}
          variables={{ first: 1000 }}
          render={({ error, props, retry }) => {
            if (error) {
              return <div>Error: {error.message}</div>;
            }

            if (!props) {
              return <Loader />;
            }

            const edges = props.poApprovalQueue?.edges || [];
            const totalCount = props.poApprovalQueue?.totalCount || 0;
            const pendingPOs = edges
              .filter(edge => edge && edge.node)
              .map(edge => edge.node);

            if (pendingPOs.length === 0) {
              return (
                <Wrapper>
                  <Header>
                    <Title>
                      {i18n.t('Purchase Order Approvals')}
                      <CountBadge>0</CountBadge>
                    </Title>
                  </Header>
                  <EmptyState>
                    <Icon type="circle-check-green" size={48} />
                    <div style={{ marginTop: '16px' }}>
                      {i18n.t('No purchase orders pending approval')}
                    </div>
                  </EmptyState>
                </Wrapper>
              );
            }

            const columns = [
              {
                id: 'poNumber',
                Header: i18n.t('PO Number'),
                accessor: 'poNumber',
                width: 120,
              },
              {
                id: 'vendor',
                Header: i18n.t('Vendor'),
                accessor: (row) => row.vendor?.name || '-',
                width: 200,
              },
              {
                id: 'totalAmount',
                Header: i18n.t('Total Amount'),
                accessor: (row) => this.formatCurrency(row.totalAmount),
                width: 150,
              },
              {
                id: 'requestedBy',
                Header: i18n.t('Requested By'),
                accessor: (row) => {
                  const user = row.requestedBy;
                  return user ? `${user.firstName} ${user.lastName}` : '-';
                },
                width: 180,
              },
              {
                id: 'createdAt',
                Header: i18n.t('Date Created'),
                accessor: (row) => this.formatDate(row.createdAt),
                width: 180,
              },
              {
                id: 'actions',
                Header: i18n.t('Actions'),
                accessor: () => null,
                width: 120,
                Cell: ({ row }) => {
                  // React Table v7 stores original data in row._original
                  const poId = row._original?.id;
                  const poNumber = row._original?.poNumber;
                  
                  if (!poId) return null;
                  
                  return (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => this.handleApprove(poId, poNumber, retry)}
                      >
                        {i18n.t('Approve')}
                      </Button>
                    </div>
                  );
                },
              },
            ];

            const tableData = pendingPOs.map(po => ({
              id: po.id,
              poNumber: po.poNumber,
              vendor: po.vendor,
              totalAmount: po.totalAmount,
              requestedBy: po.requestedBy,
              createdAt: po.createdAt,
              lineItems: po.lineItems,
            }));

            return (
              <Wrapper>
                <Header>
                  <Title>
                    {i18n.t('Purchase Order Approvals')}
                    <CountBadge>{totalCount}</CountBadge>
                  </Title>
                </Header>

                <Table
                  columns={columns}
                  data={tableData}
                  onRowClick={(row) => this.toggleRowExpansion(row.id)}
                  getRowProps={(row) => ({
                    style: {
                      cursor: 'pointer',
                      fontWeight: row.totalAmount > 5000 ? 'bold' : 'normal',
                    },
                  })}
                  renderExpandedRow={(row) => {
                    if (!expandedRows.has(row.id)) return null;

                    return (
                      <ExpandedSection>
                        <div style={{ fontWeight: 600, marginBottom: '12px' }}>
                          {i18n.t('Line Items')}
                        </div>
                        <LineItemsTable>
                          <LineItemRow>
                            <div>{i18n.t('Item Number')}</div>
                            <div>{i18n.t('Item Name')}</div>
                            <div>{i18n.t('Quantity')}</div>
                            <div>{i18n.t('Unit Price')}</div>
                            <div>{i18n.t('Total')}</div>
                          </LineItemRow>
                          {row.lineItems?.map(line => (
                            <LineItemRow key={line.id}>
                              <div>{line.item?.itemNumber || '-'}</div>
                              <div>{line.item?.name || '-'}</div>
                              <div>{line.quantity}</div>
                              <div>{this.formatCurrency(line.unitPrice)}</div>
                              <div>{this.formatCurrency(line.totalPrice)}</div>
                            </LineItemRow>
                          ))}
                        </LineItemsTable>
                      </ExpandedSection>
                    );
                  }}
                />
              </Wrapper>
            );
          }}
        />
      </Page>
    );
  }
}

export default ApprovalDashboard;
